import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Link as LinkIcon, Loader } from 'lucide-react';
import { uploadImage, resizeImage } from '../../services/storage';
import styles from './ImageUpload.module.css';

export default function ImageUpload({ 
  value, 
  onChange, 
  folder = 'articles',
  placeholder = 'URL de la imagen o sube un archivo',
  maxWidth = 1200,
  maxHeight = 800
}) {
  const [mode, setMode] = useState('url'); // 'url' o 'upload'
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleUrlChange = (e) => {
    onChange(e.target.value);
    setError('');
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    setError('');
    setUploading(true);
    setProgress(0);

    try {
      // Redimensionar si es necesario
      let fileToUpload = file;
      if (file.size > 500 * 1024) { // Si es mayor a 500KB, redimensionar
        const resizedBlob = await resizeImage(file, maxWidth, maxHeight, 0.85);
        fileToUpload = new File([resizedBlob], file.name, { type: file.type });
      }

      // Subir a Firebase
      const url = await uploadImage(fileToUpload, folder, setProgress);
      onChange(url);
      setMode('url'); // Volver a modo URL para mostrar el resultado
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    } else {
      setError('Solo se permiten archivos de imagen');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const clearImage = () => {
    onChange('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles.imageUpload}>
      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${mode === 'url' ? styles.active : ''}`}
          onClick={() => setMode('url')}
        >
          <LinkIcon size={16} />
          URL
        </button>
        <button
          type="button"
          className={`${styles.tab} ${mode === 'upload' ? styles.active : ''}`}
          onClick={() => setMode('upload')}
        >
          <Upload size={16} />
          Subir
        </button>
      </div>

      {/* Contenido según modo */}
      {mode === 'url' ? (
        <div className={styles.urlInput}>
          <input
            type="url"
            // value={value}
            value={value || ''}
            onChange={handleUrlChange}
            placeholder={placeholder}
          />
          {value && (
            <button type="button" className={styles.clearBtn} onClick={clearImage}>
              <X size={16} />
            </button>
          )}
        </div>
      ) : (
        <div
          className={`${styles.dropZone} ${dragOver ? styles.dragOver : ''} ${uploading ? styles.uploading : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleInputChange}
            className={styles.hiddenInput}
          />
          
          {uploading ? (
            <div className={styles.uploadingState}>
              <Loader size={32} className={styles.spinner} />
              <span>Subiendo... {progress}%</span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className={styles.dropContent}>
              <Upload size={32} />
              <span>Arrastra una imagen aquí</span>
              <span className={styles.subtext}>o haz clic para seleccionar</span>
              <span className={styles.hint}>JPG, PNG, GIF, WebP (máx. 5MB)</span>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Preview */}
      {value && (
        <div className={styles.preview}>
          <img src={value} alt="Preview" />
          <button type="button" className={styles.removeBtn} onClick={clearImage}>
            <X size={16} />
            Quitar imagen
          </button>
        </div>
      )}
    </div>
  );
}
